// =====================================================================
// crmCustomer — Opportunity Commercials tab
//
// Two inline editors live here:
//   - MetricsEditor      headline business metric (MEDDPICCR M)
//   - CommissionEditor   per-deal commission split + payout preview
//
// Pulled out of opportunities.jsx so the Commercials tab surface area
// stays readable on its own. CommissionEditor depends on
// window.tpCommission.commissionTotalPct (loaded earlier from
// opportunity-commission.js).
// =====================================================================

// ────────────────────────────────────────────────────────────────────
// MetricsEditor — single headline business metric for the deal (M element).
// Stored as JSON on opportunities.metrics. Having a non-empty metricLabel
// is what flips MEDDPICCR M on; the other fields are optional context.
// ────────────────────────────────────────────────────────────────────
function MetricsEditor({ opp, onChange }) {
  const initial = (opp && opp.metrics) || {};
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    metricLabel:  initial.metricLabel  || "",
    baseline:     initial.baseline     || "",
    targetValue:  initial.targetValue  || "",
    currency:     initial.currency     || (opp.currency || ""),
    source:       initial.source       || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const m = (opp && opp.metrics) || {};
    setDraft({
      metricLabel: m.metricLabel || "",
      baseline:    m.baseline    || "",
      targetValue: m.targetValue || "",
      currency:    m.currency    || (opp.currency || ""),
      source:      m.source      || "",
    });
  }, [opp.id, opp.metrics]);

  async function save() {
    setSaving(true); setError(null);
    try {
      // Drop empty strings so the column doesn't store junk; null clears.
      const payload = {};
      for (const k of ["metricLabel", "baseline", "targetValue", "currency", "source"]) {
        if (draft[k] && draft[k].trim()) payload[k] = draft[k].trim();
      }
      const updated = await tpAPI.opportunities.update(opp.id, {
        metrics: Object.keys(payload).length ? payload : null,
      });
      onChange && onChange(updated);
      setEditing(false);
    } catch (e) {
      setError(e.message || "Failed to save metric.");
    } finally {
      setSaving(false);
    }
  }

  const hasMetric = !!(initial.metricLabel && initial.metricLabel.trim());

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div className="card-title">Metric (MEDDPICCR · M)</div>
          <div className="card-sub">
            {hasMetric
              ? "M lights up when this metric is named — fill in baseline / target / source as the deal progresses."
              : "Name the business outcome this deal is sized against. Naming it lights up M."}
          </div>
        </div>
        <div className="flex-gap" style={{ gap: 6 }}>
          {!editing && <button className="btn btn-sm" onClick={() => setEditing(true)}>{hasMetric ? "Edit" : "Add metric"}</button>}
          {editing && (
            <>
              <button className="btn btn-sm" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save metric"}</button>
            </>
          )}
        </div>
      </div>
      <div className="card-body">
        {!editing ? (
          hasMetric ? (
            <div className="kv">
              <div className="kv-label">Metric</div>          <div className="kv-value">{initial.metricLabel}</div>
              <div className="kv-label">Baseline</div>        <div className="kv-value">{initial.baseline || <span className="muted">—</span>}</div>
              <div className="kv-label">Target</div>          <div className="kv-value">{initial.targetValue || <span className="muted">—</span>}</div>
              <div className="kv-label">Currency / unit</div> <div className="kv-value">{initial.currency || <span className="muted">—</span>}</div>
              <div className="kv-label">Source</div>          <div className="kv-value">{initial.source || <span className="muted">—</span>}</div>
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 13 }}>No metric named yet — M is OFF.</div>
          )
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 220px) 1fr", gap: "8px 12px", alignItems: "center" }}>
            <label>Metric label</label>
            <input type="text" value={draft.metricLabel} onChange={e => setDraft(d => ({ ...d, metricLabel: e.target.value }))}
              placeholder="e.g. Net new ARR, Logistics cost reduction" />
            <label>Baseline</label>
            <input type="text" value={draft.baseline} onChange={e => setDraft(d => ({ ...d, baseline: e.target.value }))}
              placeholder="Where they are today" />
            <label>Target</label>
            <input type="text" value={draft.targetValue} onChange={e => setDraft(d => ({ ...d, targetValue: e.target.value }))}
              placeholder="Where they want to be" />
            <label>Currency / unit</label>
            <input type="text" value={draft.currency} onChange={e => setDraft(d => ({ ...d, currency: e.target.value }))}
              placeholder="GBP, %, days, …" />
            <label>Source</label>
            <input type="text" value={draft.source} onChange={e => setDraft(d => ({ ...d, source: e.target.value }))}
              placeholder="Who confirmed it" />
          </div>
        )}
        {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// CommissionEditor — opportunity-level commission split and payout preview.
//
// The model:
//   * opp.commission is a JSON blob { owner, sdr, se, partner } of %
//   * If the opp row has no commission, we seed from
//       D.SETTINGS.commissionDefaults (org-wide), overlaying the
//       partner's defaultCommissionPct when one is attached.
//   * Saving writes the explicit JSON back so future calculations are
//     immune to subsequent default changes (historic correctness).
// ────────────────────────────────────────────────────────────────────
const COMMISSION_ROLES = [
  { key: "owner",   label: "Owner (AE)" },
  { key: "sdr",     label: "SDR" },
  { key: "se",      label: "Solutions Engineer" },
  { key: "partner", label: "Partner" },
];

function effectiveCommission(opp) {
  // Per-deal stored value wins; otherwise fall through to org defaults
  // + per-partner default for the partner slot.
  const stored   = opp.commission || {};
  const defaults = (D.SETTINGS && D.SETTINGS.commissionDefaults) || { owner: 8, sdr: 1, se: 0.5, partner: 15 };
  const partner  = opp.partnerAccountId
    ? (D.PARTNERS || []).find(p => p.accountId === opp.partnerAccountId)
    : null;
  const partnerDefault = partner && partner.defaultCommissionPct != null
    ? Number(partner.defaultCommissionPct)
    : defaults.partner;
  return {
    owner:   stored.owner   != null ? Number(stored.owner)   : Number(defaults.owner),
    sdr:     stored.sdr     != null ? Number(stored.sdr)     : Number(defaults.sdr),
    se:      stored.se      != null ? Number(stored.se)      : Number(defaults.se),
    partner: stored.partner != null ? Number(stored.partner) : Number(partnerDefault),
    _isOverridden: !!opp.commission,
  };
}

function CommissionEditor({ opp, onChange }) {
  const effective = effectiveCommission(opp);
  const [editing, setEditing] = useState(false);
  const [partnerId, setPartnerId] = useState(opp.partnerAccountId || "");
  const [rates, setRates] = useState({
    owner: effective.owner, sdr: effective.sdr, se: effective.se, partner: effective.partner,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // When the opp prop refreshes (e.g. after a save elsewhere), resync.
  useEffect(() => {
    const eff = effectiveCommission(opp);
    setRates({ owner: eff.owner, sdr: eff.sdr, se: eff.se, partner: eff.partner });
    setPartnerId(opp.partnerAccountId || "");
  }, [opp.id, opp.partnerAccountId, opp.commission]);

  const partners = D.PARTNERS || [];
  const acv = opp.acv || 0;
  const linkedPartner = partnerId ? partners.find(p => p.accountId === partnerId) : null;

  async function save() {
    setSaving(true); setError(null);
    try {
      const commission = {
        owner:   Number(rates.owner)   || 0,
        sdr:     Number(rates.sdr)     || 0,
        se:      Number(rates.se)      || 0,
        partner: Number(rates.partner) || 0,
      };
      const payload = {
        partnerAccountId: partnerId || null,
        commission,
      };
      const updated = await tpAPI.opportunities.update(opp.id, payload);
      onChange && onChange(updated);
      setEditing(false);
    } catch (e) {
      setError(e.message || "Failed to save commission.");
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    const eff = effectiveCommission(opp);
    setRates({ owner: eff.owner, sdr: eff.sdr, se: eff.se, partner: eff.partner });
    setPartnerId(opp.partnerAccountId || "");
    setError(null);
    setEditing(false);
  }

  async function clearOverride() {
    if (!opp.commission) return setEditing(false);
    setSaving(true); setError(null);
    try {
      const updated = await tpAPI.opportunities.update(opp.id, { commission: null });
      onChange && onChange(updated);
      setEditing(false);
    } catch (e) {
      setError(e.message || "Failed to clear override.");
    } finally {
      setSaving(false);
    }
  }

  // Display values come from edited state when editing, otherwise from
  // the effective (stored ∪ defaults) values so the table always shows
  // the rates the payout calculator is actually using.
  const display = editing ? rates : effective;
  const showPartnerRow = !!partnerId;
  const totalPct = window.tpCommission.commissionTotalPct(
    display,
    COMMISSION_ROLES.map(r => r.key),
    { showPartnerRow }
  );

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div className="card-title">Commission split</div>
          <div className="card-sub">
            Per-deal rates applied to ACV of {fmtMoney(acv, opp.currency)} ·
            {effective._isOverridden ? " custom override" : " org defaults"}
            {linkedPartner ? ` · partner: ${linkedPartner.accountName}` : ""}
          </div>
        </div>
        <div className="flex-gap" style={{ gap: 6 }}>
          {!editing && <button className="btn btn-sm" onClick={() => setEditing(true)}>Edit</button>}
          {editing && (
            <>
              <button className="btn btn-sm" onClick={cancel} disabled={saving}>Cancel</button>
              {effective._isOverridden && (
                <button className="btn btn-sm" onClick={clearOverride} disabled={saving} title="Revert to org defaults / partner default">Reset to defaults</button>
              )}
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save split"}</button>
            </>
          )}
        </div>
      </div>

      <div className="card-body">
        {editing && (
          <div className="form-field" style={{ maxWidth: 420 }}>
            <label>Partner on this deal</label>
            <select value={partnerId} onChange={e => setPartnerId(e.target.value)}>
              <option value="">— No partner —</option>
              {partners.map(p => (
                <option key={p.accountId} value={p.accountId}>
                  {p.accountName}{p.tier ? ` · ${p.tier}` : ""}
                </option>
              ))}
            </select>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
              Picking a partner seeds the partner % from their default rate ({linkedPartner && linkedPartner.defaultCommissionPct != null ? linkedPartner.defaultCommissionPct + "%" : "no per-partner default"}).
            </div>
          </div>
        )}

        <table className="tbl tbl-flush" style={{ marginTop: editing ? 8 : 0 }}>
          <thead><tr>
            <th>Role</th><th>Recipient</th>
            <th className="right">Rate</th>
            <th className="right">Payout on ACV</th>
          </tr></thead>
          <tbody>
            {COMMISSION_ROLES.filter(r => r.key !== "partner" || showPartnerRow).map(r => {
              const pct = Number(display[r.key]) || 0;
              const payout = acv * (pct / 100);
              let recipient = null;
              if (r.key === "owner")   recipient = getUser(opp.owner);
              if (r.key === "sdr")     recipient = getUser(opp.sdr);
              if (r.key === "se")      recipient = getUser(opp.se);
              const recipientLabel =
                r.key === "partner"
                  ? (linkedPartner ? linkedPartner.accountName : <span className="muted">—</span>)
                  : (recipient ? <>{recipient.name}</> : <span className="muted">— (unassigned)</span>);
              return (
                <tr key={r.key} style={{ cursor: "default" }}>
                  <td>{r.label}</td>
                  <td>{recipientLabel}</td>
                  <td className="right">
                    {editing ? (
                      <input
                        type="number" min="0" max="100" step="0.1"
                        value={display[r.key] == null ? "" : display[r.key]}
                        onChange={e => setRates(s => ({ ...s, [r.key]: e.target.value }))}
                        style={{ width: 80, textAlign: "right" }}
                      />
                    ) : (
                      <span className="numeric">{pct}%</span>
                    )}
                  </td>
                  <td className="right numeric">{fmtMoney(payout, opp.currency)}</td>
                </tr>
              );
            })}
            <tr style={{ cursor: "default" }}>
              <td colSpan={2}><strong>Total committed</strong></td>
              <td className="right numeric"><strong>{totalPct}%</strong></td>
              <td className="right numeric"><strong>{fmtMoney(acv * (totalPct / 100), opp.currency)}</strong></td>
            </tr>
          </tbody>
        </table>

        {totalPct > 100 && (
          <div className="form-error" style={{ marginTop: 8 }}>
            Total exceeds 100% of ACV — check the rates before saving.
          </div>
        )}
        {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}

        {!editing && (
          <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
            {effective._isOverridden
              ? "These rates are stored on the opportunity. Changing org defaults later won't affect this deal."
              : "These rates come from Admin → Customisation → Commission defaults. Click Edit to override for this deal."}
          </div>
        )}
      </div>
    </div>
  );
}

window.MetricsEditor = MetricsEditor;
window.COMMISSION_ROLES = COMMISSION_ROLES;
window.effectiveCommission = effectiveCommission;
window.CommissionEditor = CommissionEditor;
